import { useState, useEffect, useRef } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import {
  MessageCircle, Send, X, Minimize2, Maximize2, Phone, Car,
  User, Loader2, MapPin, Navigation
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { useAuthStore } from '@/store';

import { API } from '@/config/api';

// Message bubble component
const MessageBubble = ({ message, isOwnMessage }) => {
  const Icon = message.sender_type === 'driver' ? Car : User;
  
  return (
    <div className={cn(
      "flex gap-2 mb-3",
      isOwnMessage ? "flex-row-reverse" : "flex-row"
    )}>
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarFallback className={cn(
          message.sender_type === 'driver' 
            ? "bg-blue-100 text-blue-600" 
            : "bg-green-100 text-green-600"
        )}>
          <Icon className="h-4 w-4" />
        </AvatarFallback>
      </Avatar>
      
      <div className={cn(
        "max-w-[70%]",
        isOwnMessage ? "text-right" : "text-left"
      )}>
        <p className="text-xs text-muted-foreground mb-1">
          {message.sender_name}
        </p>
        <div className={cn(
          "rounded-2xl px-4 py-2 inline-block",
          isOwnMessage 
            ? "bg-primary text-primary-foreground rounded-br-md" 
            : "bg-muted rounded-bl-md"
        )}>
          <p className="text-sm">{message.content}</p>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
};

// Quick reply buttons for driver chat
const QuickReplies = ({ replies, onSelect }) => {
  return (
    <div className="flex flex-wrap gap-2 p-2 border-t bg-muted/50">
      {replies.map((reply, idx) => (
        <Button
          key={idx}
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={() => onSelect(reply)}
        >
          {reply}
        </Button>
      ))}
    </div>
  );
};

export default function DriverChat({ orderId, onClose, minimized = false }) {
  const { token, user } = useAuthStore();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isMinimized, setIsMinimized] = useState(minimized);
  const [driverInfo, setDriverInfo] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const messagesEndRef = useRef(null);

  const quickReplies = [
    "Where are you?",
    "I'm outside",
    "Please call me",
    "Can you leave at door?",
    "Thank you!"
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchDriverChat = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/driver/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setDriverInfo(response.data.participants?.driver || null);
      setDeliveryStatus(response.data.delivery_status);
    } catch (error) {
      console.error('Failed to fetch driver chat:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchDriverChat();
  }, [orderId, fetchDriverChat]);

  const sendMessage = async (content) => {
    if (!content.trim()) return;
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/chat/driver/${orderId}/send`,
        { content: content.trim(), message_type: 'text' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setMessages(prev => [...prev, response.data.message]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(newMessage);
  };

  const handleQuickReply = (reply) => {
    sendMessage(reply);
  };

  if (isMinimized) {
    return (
      <Card className="fixed bottom-4 right-4 w-auto shadow-lg z-50">
        <CardContent className="p-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMinimized(false)}
            className="gap-2"
          >
            <Car className="h-4 w-4" />
            Chat with Driver
            {messages.length > 0 && (
              <Badge variant="destructive" className="ml-1">{messages.length}</Badge>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="fixed bottom-4 right-4 w-96 shadow-lg z-50 max-h-[500px] flex flex-col">
      {/* Header */}
      <CardHeader className="p-3 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-blue-100 text-blue-600">
                <Car className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-sm">
                {driverInfo ? driverInfo.name : 'Driver Chat'}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {driverInfo 
                  ? `${driverInfo.vehicle || 'Driver'} • ${deliveryStatus || 'Assigned'}`
                  : 'No driver assigned yet'}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            {driverInfo?.phone && (
              <Button variant="ghost" size="icon" asChild className="h-8 w-8">
                <a href={`tel:${driverInfo.phone}`}>
                  <Phone className="h-4 w-4" />
                </a>
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMinimized(true)}
              className="h-8 w-8"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Messages */}
      <CardContent className="flex-1 overflow-y-auto p-3 min-h-[200px] max-h-[300px]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !driverInfo ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <Car className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">No driver assigned yet</p>
            <p className="text-xs">You'll be able to chat once a driver is on the way</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <MessageCircle className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm">Start chatting with your driver</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwnMessage={message.sender_type === 'customer'}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </CardContent>

      {/* Quick Replies */}
      {driverInfo && <QuickReplies replies={quickReplies} onSelect={handleQuickReply} />}

      {/* Input */}
      {driverInfo && (
        <form onSubmit={handleSubmit} className="p-3 border-t flex-shrink-0">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Message your driver..."
              disabled={sending}
              className="flex-1"
            />
            <Button 
              type="submit" 
              size="icon" 
              disabled={sending || !newMessage.trim()}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
